import React from 'react';
import type { PeriodStats, ReportDetailLabels } from '../../../shared/types/reports';
import { LineChart } from '../charts/LineChart';

export interface VisualSummaryProps {
  currentPeriodStats: PeriodStats;
  previousPeriodStats: PeriodStats | null;
  moodChange: number | null;
  labels: ReportDetailLabels;
}

export function VisualSummary({
  currentPeriodStats,
  previousPeriodStats,
  moodChange,
  labels,
}: VisualSummaryProps) {
  const moodChangePercent = moodChange !== null ? moodChange : 0;
  const hasImproved = moodChangePercent > 5;
  const needsAttention = moodChangePercent < -5;

  const getSummaryTitle = () => {
    if (hasImproved) return 'Your mood improved!';
    if (needsAttention) return 'Mood needs attention';
    return 'Mood remained stable';
  };

  const getTrendIcon = () => {
    if (moodChangePercent > 0) return 'bi-graph-up-arrow';
    if (moodChangePercent < 0) return 'bi-graph-down-arrow';
    return 'bi-dash-lg';
  };

  const getTrendColor = () => {
    if (moodChangePercent > 0) return 'text-success';
    if (moodChangePercent < 0) return 'text-warning';
    return '';
  };

  // Prepare mood timeline data for the chart
  const timelineData = currentPeriodStats.moodTimeline
    .filter(entry => entry.mood !== null)
    .map(entry => ({
      label: new Date(entry.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      value: entry.mood!,
    }));

  return (
    <div className="card border-0 shadow-sm mb-4 overflow-hidden">
      {/* Purple header bar */}
      <div
        className="px-3 px-sm-4 py-2 py-sm-3 text-white"
        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
      >
        <div className="d-flex align-items-center flex-wrap">
          <i className={`bi ${getTrendIcon()} fs-5 me-2`}></i>
          <h6 className="mb-0 fw-bold">{getSummaryTitle()}</h6>
          {previousPeriodStats?.averageMood !== null && moodChange !== null && (
            <span className="ms-2 opacity-75 small">
              ({Math.abs(moodChangePercent).toFixed(1)}%{' '}
              {moodChangePercent > 0 ? 'up' : moodChangePercent < 0 ? 'down' : 'same'})
            </span>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="card-body p-3 p-sm-4">
        <div className="row align-items-center">
          <div className="col-12 col-md-4 mb-3 mb-md-0">
            <div className="d-flex justify-content-center justify-content-md-start gap-4">
              <div className="text-center">
                <div className="small text-muted">{labels.currentPeriod} Avg</div>
                <div className="fs-3 fw-bold" style={{ color: '#667eea' }}>
                  {currentPeriodStats.averageMood !== null
                    ? currentPeriodStats.averageMood.toFixed(0)
                    : '-'}
                </div>
              </div>
              <div className="text-center">
                <div className="small text-muted">{labels.moodEntries}</div>
                <div className="fs-3 fw-bold" style={{ color: '#667eea' }}>
                  {currentPeriodStats.moodCount}
                </div>
              </div>
            </div>
          </div>
          {timelineData.length > 0 && (
            <div className="col-12 col-md-8">
              <div style={{ position: 'relative', height: 140 }}>
                <LineChart
                  data={timelineData}
                  height={140}
                  minY={0}
                  maxY={100}
                  useMoodColors={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VisualSummary;
